import { workspaceCodec, workspaceBundleCodec, newWorkspaceCodec, updatedWorkspaceCodec } from './workspaceCodecs';
import type { DecodedWorkspace } from './workspaceCodecs';
import { WorkspaceRecord, WorkspaceConfig } from '@thinkalpha/platform-ws-client/contracts/workspace.js';
import type { WorkspaceBundle, Workspace } from '@thinkalpha/platform-ws-client/contracts/workspace.js';
import type { ResourceQuery, ResourceQueryResponseWithMeta } from '@thinkalpha/ui-types/contracts/resource-query';
import type {
    NewWorkspace,
    UpdatedWorkspace,
    Workspace as LocalContractsWorkspace,
} from '@thinkalpha/ui-types/contracts/workspace';
import { isAxiosError } from 'axios';
import { defer, map } from 'rxjs';
import type { Observable } from 'rxjs';
import { processQuery } from 'src/lib/paging';
import { container } from 'src/StaticContainer';
import { z } from 'zod';

const log = container.get('Logger').getSubLogger({ name: 'api:workspaces' });

const api = container.get('PlatformApi');

// Parsed from the plain-JSON PlatformApi instance with the ws-client zod codecs instead
// of a pattern-matching reviver, so a workspace named "2026-08-10" stays a string rather
// than becoming a js-joda date.
const WorkspaceRecordsResponse = z.object({
    results: z.array(WorkspaceRecord),
    count: z.number(),
});

interface WorkspaceDimensions {
    columnCount: number;
    rowCount: number;
}

interface MaybeWithDimensions {
    dimensions?: WorkspaceDimensions;
}

export type LocalWorkspaceWithServerMetadata = LocalContractsWorkspace & Pick<Workspace, 'isUserDefault'>;
export type LocalWorkspaceBundle = Omit<WorkspaceBundle, 'workspace'> & {
    workspace: LocalWorkspaceWithServerMetadata;
};

const addDimensionsToWorkspace = <T extends NewWorkspace>(workspace: T & MaybeWithDimensions) => ({
    ...workspace,
    dimensions: workspace.dimensions ?? { columnCount: 1, rowCount: 1 },
});

const stripKeyboardCommands = (workspace: DecodedWorkspace): DecodedWorkspace => ({
    ...workspace,
    containers: workspace.containers.map((widgetContainer) => ({
        ...widgetContainer,
        tabs: widgetContainer.tabs.map((tab) => {
            const widget = { ...tab.widget };
            if ('keyboardCommands' in widget) delete widget.keyboardCommands;
            return { ...tab, widget };
        }),
    })),
});

export const mapToCurrentUIExpectations = (workspace: DecodedWorkspace): LocalWorkspaceWithServerMetadata => {
    const normalizedWorkspace = stripKeyboardCommands(workspace);
    const { dimensions, ...rest } = normalizedWorkspace;

    if (dimensions && (dimensions.columnCount !== 1 || dimensions?.rowCount !== 1)) {
        rest.containers = rest.containers.map((container) => {
            const { layout, ...rest } = container;
            return {
                ...rest,
                layout: {
                    ...layout,
                    h: layout.h / dimensions.rowCount,
                    w: layout.w / dimensions.columnCount,
                    x: layout.x / dimensions.columnCount,
                    y: layout.y / dimensions.rowCount,
                },
            };
        });
    }

    // @ts-expect-error - The local contract types are all out of wack
    // We could try to map the types but I'm concerned that the app currently _works_ using the local contract types
    // and modifying responses to adhere to those types could have consequences.
    return rest;
};

export async function getDefaultWorkspace(expandImports?: true): Promise<LocalContractsWorkspace | null> {
    const response = await api.get<z.input<typeof workspaceCodec>>(
        `/workspaces/default${expandImports ? '?$expand=imports' : ''}`,
        {
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        },
    );

    return response.status === 404 ? null : mapToCurrentUIExpectations(workspaceCodec.decode(response.data));
}

export async function getWorkspaceById(id: string): Promise<LocalContractsWorkspace> {
    const workspace = await api
        .get<z.input<typeof workspaceCodec>>(`/workspaces/${id}?$expand=imports`)
        .then((res) => mapToCurrentUIExpectations(workspaceCodec.decode(res.data)));

    return workspace;
}

export async function getWorkspaceBundle(id: 'default' | string): Promise<LocalWorkspaceBundle> {
    const bundle = await api
        .get<z.input<typeof workspaceBundleCodec>>(`/workspaces/${id}/bundle`)
        .then((res) => workspaceBundleCodec.decode(res.data));

    return {
        ...bundle,
        workspace: mapToCurrentUIExpectations(bundle.workspace),
    };
}

/**
 * This is an ugly hack. Cover your eyes.
 */
export function mapWatchlistDisplayModeToWidgetType(workspace: LocalContractsWorkspace): LocalContractsWorkspace {
    for (const container of workspace.containers) {
        for (const tab of container.tabs) {
            if (tab.widget.type === 'results' && (tab.widget as any).displayMode === 'watchlist') {
                (tab.widget as any).type = 'watchlist';
            }
        }
    }
    return workspace;
}

export async function createWorkspace(workspace: NewWorkspace): Promise<LocalContractsWorkspace> {
    const res = await api.post<z.input<typeof workspaceCodec>>(
        `/workspaces?$expand=imports`,
        newWorkspaceCodec.encode(addDimensionsToWorkspace(workspace)),
    );
    return mapToCurrentUIExpectations(workspaceCodec.decode(res.data));
}

export async function saveWorkspace(
    workspace: UpdatedWorkspace,
    expandImports?: true,
): Promise<LocalContractsWorkspace> {
    const payload = {
        ...updatedWorkspaceCodec.encode(addDimensionsToWorkspace(workspace)),
        channels: [],
    };
    try {
        const res = await api.put<z.input<typeof workspaceCodec>>(
            `/workspaces/${workspace.id}${expandImports ? '?$expand=imports' : ''}`,
            payload,
        );
        return mapToCurrentUIExpectations(workspaceCodec.decode(res.data));
    } catch (error) {
        if (isAxiosError(error)) {
            log.error('Failed to save workspace', {
                workspaceId: workspace.id,
                payload,
                responseStatus: error.response?.status,
                responseData: error.response?.data,
            });
        } else {
            log.error('Failed to save workspace', {
                workspaceId: workspace.id,
                payload,
                error,
            });
        }
        throw error;
    }
}

export function deleteWorkspace(id: string): Observable<void> {
    return defer(() => api.delete(`/workspaces/${id}`)).pipe(
        map(() => {
            /* void */
        }),
    );
}

export function getWorkspacesQueryP(query: ResourceQuery): Promise<ResourceQueryResponseWithMeta<WorkspaceRecord>> {
    return api
        .get(`/workspaces/query${processQuery({ ...query, includeMeta: true })}`)
        .then((x) => WorkspaceRecordsResponse.parse(x.data));
}

export function getWorkspaceConfig(): Promise<WorkspaceConfig> {
    return api
        .get<z.input<typeof WorkspaceConfig>>('/workspaces/config')
        .then((res) => WorkspaceConfig.decode(res.data));
}

export async function getContainerTemplates(expandImports?: true): Promise<LocalContractsWorkspace[]> {
    const res = await api.get<z.input<typeof workspaceCodec>[]>(
        `/workspaces/templates${expandImports ? '?$expand=imports' : ''}`,
    );
    return res.data
        .map((workspace) => mapToCurrentUIExpectations(workspaceCodec.decode(workspace)))
        .map(mapWatchlistDisplayModeToWidgetType);
}
